import React, { useEffect, useState } from 'react';
import { roundQuantityWithThreshold } from '../../utils/forecastMath.js';

const ProductionModal = ({ open, item, onClose, onApplyStock, onViewHourly }) => {
  const [stockValue, setStockValue] = useState('');

  useEffect(() => {
    if (open && item) {
      setStockValue(item.stock && item.stock > 0 ? String(item.stock) : '');
    }
  }, [open, item]);

  if (!open || !item || item.isAzucarGlass) {
    return null;
  }

  const itemStock = Number.isFinite(item.stock) && item.stock > 0 ? item.stock : 0;
  const parsedStock = Number.parseInt(stockValue, 10);
  const liveStockValue = Number.isFinite(parsedStock) && parsedStock >= 0 ? parsedStock : 0;
  const adjustedForecast = Number.isFinite(item.adjustedForecast)
    ? item.adjustedForecast
    : Number.isFinite(item.forecastExact)
      ? item.forecastExact
      : Number.isFinite(item.quantityExact)
        ? item.quantityExact
        : 0;
  const rawToProduce = Math.max(0, adjustedForecast - liveStockValue);
  const liveToProduce = item.isAzucarGlass ? rawToProduce : roundQuantityWithThreshold(rawToProduce);
  const liveToProduceDisplay = item.isAzucarGlass
    ? rawToProduce.toFixed(2)
    : liveToProduce;
  const isStockSynced = itemStock === liveStockValue;
  const stockoutText = (() => {
    if (!isStockSynced) {
      return 'Actualizando cobertura...';
    }
    if (liveStockValue <= 0) {
      return 'Captura tu stock';
    }
    const info = item.stockout;
    if (!info || !info.formattedTime) {
      return item.stockDurationText || 'Sin datos de tiempo';
    }
    const verb = info.totalMinutes && info.totalMinutes > 360 ? 'Dura' : 'Se agota en';
    return `${verb} ${info.formattedTime}`;
  })();

  const handleStockChange = (value) => {
    const sanitized = value === '' ? '' : Math.max(0, Number.parseInt(value, 10) || 0);
    setStockValue(sanitized === '' ? '' : String(sanitized));
    onApplyStock?.(item.name, sanitized);
  };

  const handleApply = () => {
    const sanitized = stockValue === '' ? '' : Math.max(0, Number.parseInt(stockValue, 10) || 0);
    onApplyStock?.(item.name, sanitized);
    onClose?.();
  };

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content ux-modal production-modal">
        <div className="ux-modal-header">
          <div className="ux-header-content">
            <div className="ux-header-title-row">
              {item.imagen_url ? (
                <img src={item.imagen_url} alt={item.name} className="ux-item-image" />
              ) : (
                <div className="summary-item-image-placeholder" />
              )}
              <div className="ux-title-content">
                <h2 id="modal-title">Editar {item.name}</h2>
                <p className="ux-subtitle">{item.unitLabel || 'Unidades'} • {item.lifespan}</p>
              </div>
            </div>
          </div>
          <button className="ux-close-btn" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <div className="ux-modal-body">
          <div className="production-summary-grid">
            <div className="production-chip subtle">
              <span className="chip-label">Pronóstico</span>
              <p className="chip-value">{item.forecastDisplay ?? Math.round(item.forecastExact || 0)}</p>
              <span className="chip-meta">{item.unitLabel || 'Unidades'}</span>
            </div>
            <div className="production-chip primary">
              <span className="chip-label">A producir</span>
              <p className="chip-value">{liveToProduceDisplay}</p>
              <span className="chip-meta">Se ajusta al capturar stock</span>
            </div>
            <div className="production-chip ghost">
              <span className="chip-label">Cobertura</span>
              <p className="chip-value chip-value-small">{stockoutText}</p>
              <span className="chip-meta">Con el stock actual</span>
            </div>
          </div>
          <div className="ux-motivo-row stock-entry">
            <div className="ux-motivo-info">
              <div className="ux-motivo-icon">
                <i data-lucide="package" />
              </div>
              <div className="ux-motivo-details">
                <h4 className="ux-motivo-name">Stock actual</h4>
                <p className="ux-motivo-description">
                  Ingresa el stock actualizado para recalcular la producción sugerida.
                </p>
                <div className="ux-stockout-chip">
                  <i data-lucide="activity" />
                  {stockoutText}
                </div>
              </div>
            </div>
            <div className="ux-input-container stacked">
              <label className="ux-input-label" htmlFor="current-stock-input">Unidades en stock</label>
              <div className="ux-input-shell">
                <input
                  id="current-stock-input"
                  type="number"
                  className="ux-adjustment-input"
                  placeholder="0"
                  min="0"
                  step="1"
                  value={stockValue}
                  onChange={(event) => handleStockChange(event.target.value)}
                />
                <span className="ux-percentage-symbol">{item.unitLabel || 'unidades'}</span>
              </div>
              <p className="ux-inline-help">El cálculo se actualiza en tiempo real.</p>
            </div>
          </div>
        </div>
        <div className="ux-modal-footer">
          <div className="ux-footer-left">
            <button
              type="button"
              className="ux-btn ux-btn-secondary"
              onClick={() => onViewHourly?.(item.name)}
            >
              <i data-lucide="bar-chart-3" /> Ver detalle por horas
            </button>
          </div>
          <div className="ux-footer-right">
            <button type="button" className="ux-btn ux-btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="button" className="ux-btn ux-btn-primary" onClick={handleApply}>
              Actualizar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionModal;
